import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Application-level Sentry event management
 * Provides typed wrappers for capturing errors, logs, and metrics
 */
@Injectable({ providedIn: 'root' })
export class SentryEventService {
  /**
   * Capture an exception with context
   */
  captureException(
    error: Error | unknown,
    context?: Record<string, unknown>,
    tags?: Record<string, string>
  ): void {
    if (context) {
      Sentry.setContext('error_context', context);
    }
    if (tags) {
      Sentry.setTags(tags);
    }
    Sentry.captureException(error);
  }

  /**
   * Capture a message
   */
  captureMessage(
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
    context?: Record<string, unknown>
  ): void {
    if (context) {
      Sentry.setContext('message_context', context);
    }
    Sentry.captureMessage(message, level);
  }

  /**
   * Set user context
   */
  setUser(userId: string, email?: string, username?: string): void {
    Sentry.setUser({
      id: userId,
      email,
      username,
    });
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    Sentry.setUser(null);
  }

  /**
   * Add a breadcrumb for tracking user actions
   */
  addBreadcrumb(
    message: string,
    category: string,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
  ): void {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000,
    });
  }

  /**
   * Record a custom metric
   */
  recordMetric(name: string, value: number, unit?: string): void {
    try {
      Sentry.metrics.gauge(name, value, unit ? { unit } : undefined);
    } catch (error) {
      console.warn(`Failed to record metric ${name}:`, error);
    }
  }

  /**
   * Record a counter metric
   */
  recordCounter(name: string, value = 1): void {
    try {
      Sentry.metrics.count(name, value);
    } catch (error) {
      console.warn(`Failed to record counter ${name}:`, error);
    }
  }

  /**
   * Wrap a function in a performance span for tracing.
   * Returns the function's result.
   */
  startSpan<T>(name: string, operation: string, fn: () => T): T {
    return Sentry.startSpan({ name, op: operation }, fn);
  }
}
